// handleRealtimeEvent.js
'use strict';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

import getOperationData from '../selector/getOperationData.js';

import { RealtimeEventInformation } from '../resource/realtimeConstants.js';
import { realtime as realtimeDebug } from '../resource/debug.js';

/**
 * Batched event map.
 * Maps cache keys (format: `{event}:{id}`) to batched event objects that accumulate.
 * @type {Map<string, { pendings: Array, cooldownUntil: number, timer: number }>}
 */
const batchedEventMap = new Map();

const log = realtimeDebug.extend('log:handleRealtimeEvent');
const errorLog = realtimeDebug.extend('error:handleRealtimeEvent');

/**
 * Get cache key.
 * @param {object} payload - action payload.
 * @param {RealtimeEvent} payload.event - realtime event name.
 * @param {string} payload.id - object id. The meaning varies depending on the realtime event type. e.g. stream id, message id, etc.
 * @return {string} Cache key.
 */
const getCacheKey = ({ event, id } = {}) => `${event}:${id}`;

/**
 * Handle realtime event.
 * @kind action
 * @param {object} payload - action payload.
 * @param {string} payload.id - object id. The meaning varies depending on the realtime event type. e.g. stream id, message id, etc.
 * @param {RealtimeEvent} payload.event - realtime event name.
 * @param {object} payload.data - realtime event data.
 * @param {'realtime'|'api'} payload.via - realtime data via.
 * @return {Promise} Action promise.
 */
const handleRealtimeEvent =
  (payload = {}) =>
  async (dispatch, getState) => {
    try {
      log('handle realtime event', payload);

      const { id, event, data } = payload;

      const { lazyHandler, featureName, dataRecordName } =
        RealtimeEventInformation[event] || {};

      if (!lazyHandler || !featureName || !dataRecordName) {
        log('missing corresponding realtime event information', {
          ...payload,
          lazyHandler,
          featureName,
          dataRecordName,
        });
        return dispatch({ type: '' });
      }

      const currentUpdatedAt =
        getOperationData(
          getState(),
          ['realtime', 'dataRecord', featureName, id, dataRecordName],
          'updatedAt'
        ) || 0;
      const needUpdate = data.timestamp > currentUpdatedAt;

      if (!needUpdate) {
        log('the current data is fresh enough', {
          ...payload,
          currentUpdatedAt,
        });
        return dispatch({ type: '' });
      }

      await dispatch(batchRealtimeEvent(payload));

      log('handle realtime event done', payload);
    } catch (error) {
      errorLog('encounter error on handle realtime event', {
        error,
        payload,
      });
    }
  };

/**
 * Batch realtime event.
 * Accumulates events with the same cache key(`{event}:{id}`) and processes them in batches after a cooldown period.
 * @kind action
 * @param {object} payload - action payload.
 * @param {string} payload.id - object id. The meaning varies depending on the realtime event type. e.g. stream id, message id, etc.
 * @param {RealtimeEvent} payload.event - realtime event name.
 * @param {object} payload.data - realtime event data.
 * @param {'realtime'|'api'} payload.via - realtime data via.
 * @return {Promise} Action promise.
 */
const batchRealtimeEvent =
  (payload = {}) =>
  async dispatch => {
    const { id, event, data } = payload;
    const key = getCacheKey({ event, id });
    const { batchMsec = 0 } = RealtimeEventInformation[event] || {};

    if (!batchedEventMap.has(key)) {
      batchedEventMap.set(key, {
        pendings: [],
        cooldownUntil: 0,
        timer: null,
      });
    }

    const { pendings, cooldownUntil, timer } = batchedEventMap.get(key);

    pendings.push(data);

    if (Date.now() >= cooldownUntil) {
      batchedEventMap.set(key, {
        pendings: [],
        cooldownUntil: Date.now() + batchMsec,
        timer: null,
      });
      dispatch(handleBatchedRealtimeEvents({ id, event, pendings }));
    } else {
      const delayMsec = cooldownUntil - Date.now();

      if (!timer) {
        batchedEventMap.get(key).timer = setTimeout(() => {
          batchedEventMap.set(key, {
            pendings: [],
            cooldownUntil: Date.now() + batchMsec,
            timer: null,
          });
          dispatch(handleBatchedRealtimeEvents({ id, event, pendings }));
        }, delayMsec);
      }

      log('put the event data into batch', payload);
    }
  };

/**
 * Handle batched realtime events.
 * @kind action
 * @param {object} payload - action payload.
 * @param {string} payload.id - object id. The meaning varies depending on the realtime event type. e.g. stream id, message id, etc.
 * @param {RealtimeEvent} payload.event - realtime event name.
 * @param {object[]} payload.pendings - batched event data.
 * @return {Promise} Action promise.
 */
const handleBatchedRealtimeEvents =
  ({ id, event, pendings } = {}) =>
  async dispatch => {
    log('handle batched realtime events', {
      id,
      event,
      pendings,
    });

    if (pendings.length === 0) return dispatch({ type: '' });

    const { lazyHandler, featureName, dataRecordName } =
      RealtimeEventInformation[event] || {};

    const handler = await lazyHandler();
    const latestUpdatedAt = Math.max(
      ...pendings.map(item => item.timestamp || 0)
    );

    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['realtime', 'dataRecord', featureName, id, dataRecordName],
        data: {
          updatedAt: latestUpdatedAt,
        },
      },
    });
    dispatch(
      handler({
        id,
        event,
        data: pendings,
      })
    );
  };

export default handleRealtimeEvent;
